import { useEffect, useRef, useState, useCallback } from "react";

export const useMarketingPageEffects = ({ loaderDone }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [heroVisible, setHeroVisible] = useState(false);

  const heroBgRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    if (loaderDone) {
      setHeroVisible(true);
    }
  }, [loaderDone]);

  useEffect(() => {
    const onScroll = () => {
      setNavScrolled(window.scrollY > 40);
      if (heroBgRef.current) {
        heroBgRef.current.style.transform = `translateY(${window.scrollY * 0.3}px) scale(1.05)`;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    root.querySelectorAll(".reveal,.reveal-left,.reveal-right").forEach((el) => {
      revealObserver.observe(el);
    });

    const statObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.target);
          if (Number.isNaN(target)) {
            statObserver.unobserve(el);
            return;
          }
          const isDecimal = target % 1 !== 0;
          const duration = 1800;
          const steps = 60;
          let step = 0;
          const timer = setInterval(() => {
            step += 1;
            const eased = 1 - (1 - step / steps) ** 3;
            const current = target * eased;
            if (target === 98) el.textContent = `${Math.floor(current)}%`;
            else if (target === 3200) el.textContent = `${Math.floor(current).toLocaleString()}+`;
            else if (isDecimal) el.textContent = `${current.toFixed(1)}d`;
            if (step >= steps) clearInterval(timer);
          }, duration / steps);
          statObserver.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    root.querySelectorAll(".stat-num[data-target]").forEach((el) => {
      statObserver.observe(el);
    });

    return () => {
      revealObserver.disconnect();
      statObserver.disconnect();
    };
  }, [loaderDone]);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);
  const toggleFaq = useCallback((index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleMagneticMove = useCallback((e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    btn.style.transform = `translate(${dx * 0.18}px, ${dy * 0.18}px)`;
  }, []);

  const handleMagneticLeave = useCallback((e) => {
    e.currentTarget.style.transform = "";
  }, []);

  return {
    menuOpen,
    navScrolled,
    openFaqIndex,
    heroVisible,
    heroBgRef,
    pageRef,
    toggleMenu,
    toggleFaq,
    handleMagneticMove,
    handleMagneticLeave,
  };
};
